import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class ComWizardSteps implements Plugin {
  private readonly steps: NodeListOf<HTMLElement>;
  private readonly activeItemClass: string;
  private readonly inactiveItemClass: string;
  private readonly activeContentClass: string;
  private readonly inactiveContentClass: string;
  private readonly eventName: string;
  private currentStepIndex: number = 0;

  constructor(private readonly element: HTMLElement) {
    // If specific item classes are provided in dataset, use them, otherwise fallback to generic
    this.activeItemClass =
      element.dataset.activeItemClass || "com-wizard-steps-item--state-active";
    this.inactiveItemClass =
      element.dataset.inactiveItemClass ||
      "com-wizard-steps-item--state-inactive";

    this.activeContentClass = element.dataset.activeContentClass || "";
    this.inactiveContentClass =
      element.dataset.inactiveContentClass || "hidden";
    this.eventName = element.dataset.eventName || "com-wizard-steps:change";

    this.steps = element.querySelectorAll("[data-wizard-step]");

    this.steps.forEach((step, index) => {
      const itemRoot = step.hasAttribute("data-wizard-step-root")
        ? step
        : step.closest("[class*='-item']") || step;

      if (itemRoot && itemRoot.classList.contains(this.activeItemClass)) {
        this.currentStepIndex = index;
      }
    });
  }

  init(): void {
    if (this.steps.length === 0) return;
    this.setupEvents();
    this.updateStepsUI();
    this.updateContentVisibility(this.currentStepIndex);
  }

  private setupEvents(): void {
    this.steps.forEach((step, index) => {
      step.addEventListener("click", () => {
        this.handleStepClick(index);
      });
    });
  }

  private handleStepClick(index: number): void {
    if (index === this.currentStepIndex) return;
    if (this.steps[index].dataset.navigable === "false") return;

    const previousIndex = this.currentStepIndex;
    this.currentStepIndex = index;

    this.updateStepsUI();
    this.updateContentVisibility(index);
    this.dispatchChange(index, previousIndex);
  }

  private updateStepsUI(): void {
    this.steps.forEach((step, index) => {
      const shouldBeActive = index <= this.currentStepIndex;

      const itemRoot = step.hasAttribute("data-wizard-step-root")
        ? step
        : step.closest("[class*='-item']") || step;

      if (itemRoot) {
        if (shouldBeActive) {
          itemRoot.classList.add(this.activeItemClass);
          if (this.inactiveItemClass) {
            itemRoot.classList.remove(this.inactiveItemClass);
          }
          itemRoot.setAttribute("aria-current", "step");

          const numberElement = itemRoot.querySelector('[class*="-number"]');
          if (numberElement) {
            const activeNumClass = this.activeItemClass.replace(
              "-item-",
              "-number-",
            );
            const inactiveNumClass = this.inactiveItemClass.replace(
              "-item-",
              "-number-",
            );
            numberElement.classList.add(activeNumClass);
            if (inactiveNumClass)
              numberElement.classList.remove(inactiveNumClass);
          }
        } else {
          itemRoot.classList.remove(this.activeItemClass);
          if (this.inactiveItemClass) {
            itemRoot.classList.add(this.inactiveItemClass);
          }
          itemRoot.removeAttribute("aria-current");

          const numberElement = itemRoot.querySelector('[class*="-number"]');
          if (numberElement) {
            const activeNumClass = this.activeItemClass.replace(
              "-item-",
              "-number-",
            );
            const inactiveNumClass = this.inactiveItemClass.replace(
              "-item-",
              "-number-",
            );
            numberElement.classList.remove(activeNumClass);
            if (inactiveNumClass) numberElement.classList.add(inactiveNumClass);
          }
        }
      }
    });
  }

  private updateContentVisibility(activeIndex: number): void {
    this.steps.forEach((step, index) => {
      const targetId = step.dataset.target;
      if (!targetId) return;

      const content = document.querySelector(targetId);
      if (content) {
        if (index === activeIndex) {
          content.classList.remove(this.inactiveContentClass);
          if (this.activeContentClass)
            content.classList.add(this.activeContentClass);
        } else {
          if (this.activeContentClass)
            content.classList.remove(this.activeContentClass);
          content.classList.add(this.inactiveContentClass);
        }
      }
    });
  }

  private dispatchChange(activeIndex: number, previousIndex: number): void {
    const activeStep = this.steps[activeIndex];
    const targetId = activeStep.dataset.target;
    const customData = activeStep.dataset.customData
      ? JSON.parse(activeStep.dataset.customData)
      : {};

    this.element.dispatchEvent(
      new CustomEvent(this.eventName, {
        detail: {
          stepIndex: activeIndex + 1,
          previousIndex: previousIndex + 1,
          targetId,
          ...customData,
        },
        bubbles: true,
      }),
    );
  }
}

PluginRegistry.register("comWizardSteps", ComWizardSteps);
